import { useEffect } from 'react';
import '@/App.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LoginModalProvider } from '@/context/LoginModalContext';
import { I18nProvider } from '@/context/I18nContext';
import { ShareConfigProvider } from '@/context/ShareConfigContext';
import { CategoriesProvider } from '@/context/CategoriesContext';
import { CartProvider } from '@/context/CartContext';
import { PostMessageBridge } from '@/components/common/PostMessageBridge';
import { Toaster } from '@/components/ui/sonner';

// Public
import HomePage from '@/pages/HomePage';
import SearchPage from '@/pages/SearchPage';
import CategoryPage from '@/pages/CategoryPage';
import AdDetailPage from '@/pages/AdDetailPage';
import ProfilePage from '@/pages/ProfilePage';
import OrderSuccessPage from '@/pages/OrderSuccessPage';

// Auth
import LoginPage from '@/pages/LoginPage';
import OkUserAcceso from '@/pages/OkUserAcceso';
import ChangePinPage from '@/pages/ChangePinPage';
import AdminLoginPage from '@/pages/admin/AdminLoginPage';

// User dashboard
import DashboardLayout from '@/pages/dashboard/DashboardLayout';
import PanelLayout from '@/pages/panel/PanelLayout';
import DashboardHome from '@/pages/dashboard/DashboardHome';
import MyAdsPage from '@/pages/dashboard/MyAdsPage';
import NewAdPage from '@/pages/dashboard/NewAdPage';
import EditAdPage from '@/pages/dashboard/EditAdPage';
import FavoritesPage from '@/pages/dashboard/FavoritesPage';
import PurchasesPage from '@/pages/dashboard/PurchasesPage';
import SalesPage from '@/pages/dashboard/SalesPage';
import RatingsPage from '@/pages/dashboard/RatingsPage';
import MessagesPage from '@/pages/dashboard/MessagesPage';
import NextLevelHub from '@/pages/dashboard/NextLevelHub';
import ProfileEditPage from '@/pages/dashboard/ProfileEditPage';
import ModerationPage from '@/pages/dashboard/ModerationPage';
import KycPage from '@/pages/dashboard/KycPage';
import OffersPage from '@/pages/dashboard/OffersPage';
import InvoicesPage from '@/pages/dashboard/InvoicesPage';
import ProPage from '@/pages/dashboard/ProPage';
import DashboardStorefront from '@/pages/dashboard/DashboardStorefront';
import DashboardLivestream from '@/pages/dashboard/DashboardLivestream';
import DashboardWallet from '@/pages/dashboard/DashboardWallet';
import DashboardCashback from '@/pages/dashboard/DashboardCashback';
import DashboardServicesProvider from '@/pages/dashboard/DashboardServicesProvider';
import DashboardServiceOrders from '@/pages/dashboard/DashboardServiceOrders';
import DashboardEnvios from '@/pages/dashboard/DashboardEnvios';
import DashboardFlashSales from '@/pages/dashboard/DashboardFlashSales';
import DashboardMyServices from '@/pages/dashboard/DashboardMyServices';
import DashboardAddresses from '@/pages/dashboard/DashboardAddresses';
import DashboardComisiones from '@/pages/dashboard/DashboardComisiones';
import DashboardImpuestos from '@/pages/dashboard/DashboardImpuestos';
import ServicesHubPage from '@/pages/ServicesHubPage';
import ServiceProviderPage from '@/pages/ServiceProviderPage';
import StatusPage from '@/pages/StatusPage';
import PlansPage from '@/pages/PlansPage';
import SuperpoderesPage from '@/pages/SuperpoderesPage';
import OnlinePage from '@/pages/OnlinePage';

// Public user page
import PublicUserPage from '@/pages/PublicUserPage';
import StorefrontPage from '@/pages/StorefrontPage';
import LivestreamPage from '@/pages/LivestreamPage';

// Super admin
import SuperAdminLayout from '@/pages/admin/SuperAdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminAds from '@/pages/admin/AdminAds';
import AdminCategories from '@/pages/admin/AdminCategories';
import AdminOrders from '@/pages/admin/AdminOrders';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminBanners from '@/pages/admin/AdminBanners';
import AdminOAuthSSO from '@/pages/admin/AdminOAuthSSO';
import AdminConnect from '@/pages/admin/AdminConnect';
import AdminApiConnect from '@/pages/admin/AdminApiConnect';
import AdminWidgets from '@/pages/admin/AdminWidgets';
import AdminKyc from '@/pages/admin/AdminKyc';
import AdminReports from '@/pages/admin/AdminReports';
import AdminFlashSales from '@/pages/admin/AdminFlashSales';
import AdminFactura from '@/pages/admin/AdminFactura';
import AdminEmbed from '@/pages/admin/AdminEmbed';
import AdminLive from '@/pages/admin/AdminLive';
import AdminStorefront from '@/pages/admin/AdminStorefront';
import AdminWallet from '@/pages/admin/AdminWallet';
import AdminCashback from '@/pages/admin/AdminCashback';
import AdminServices from '@/pages/admin/AdminServices';
import AdminPlans from '@/pages/admin/AdminPlans';
import AdminEnvios from '@/pages/admin/AdminEnvios';
import AdminComisiones from '@/pages/admin/AdminComisiones';
import AdminImpuestos from '@/pages/admin/AdminImpuestos';
import AdminWithdrawals from '@/pages/admin/AdminWithdrawals';
import AdminOkPro from '@/pages/admin/AdminOkPro';
import AdminEcartPay from '@/pages/admin/AdminEcartPay';
import AdminSellerBanners from '@/pages/admin/AdminSellerBanners';
import AdminOkDelivery from '@/pages/admin/AdminOkDelivery';
import AdminSuperCategorias from '@/pages/admin/AdminSuperCategorias';
import AdminComisionesOkGo from '@/pages/admin/AdminComisionesOkGo';
import AdminOkNegocios from '@/pages/admin/AdminOkNegocios';
import AdminPlatformApps from '@/pages/admin/AdminPlatformApps';
import AdminOkBot from '@/pages/admin/AdminOkBot';
import AdminOkChat from '@/pages/admin/AdminOkChat';
import AdminOkLovendo from '@/pages/admin/AdminOkLovendo';
import OkBotChatWidget from '@/components/common/OkBotChatWidget';
import OkChatWidget from '@/components/common/OkChatWidget';
import DashboardOkPro from '@/pages/dashboard/DashboardOkPro';
import DashboardSellerBanners from '@/pages/dashboard/DashboardSellerBanners';
import WidgetView from '@/pages/WidgetView';
import CartPage from '@/pages/CartPage';
import ListsPage from '@/pages/dashboard/ListsPage';

// OkGo platform
import OkGoHomePage from '@/pages/OkGoHomePage';
import OkNegocioProfile from '@/pages/aliado/OkNegocioProfile';
import OkNegocioRegister from '@/pages/aliado/OkNegocioRegister';

// OkNegocio dashboard
import AliadoDashboardLayout, { AliadoGuard } from '@/pages/aliado/AliadoDashboardLayout';
import AliadoDashboardHome from '@/pages/aliado/AliadoDashboardHome';
import AliadoOrdersPage from '@/pages/aliado/AliadoOrdersPage';
import AliadoProductsPage from '@/pages/aliado/AliadoProductsPage';
import AliadoCategoriesPage from '@/pages/aliado/AliadoCategoriesPage';
import AliadoBranchesPage from '@/pages/aliado/AliadoBranchesPage';
import AliadoSalesPage from '@/pages/aliado/AliadoSalesPage';
import AliadoSquadPage from '@/pages/aliado/AliadoSquadPage';
import AliadoCartasPage from '@/pages/aliado/AliadoCartasPage';
import AliadoPasillosPage from '@/pages/aliado/AliadoPasillosPage';
import AliadoSettingsPage from '@/pages/aliado/AliadoSettingsPage';
import AliadoOkDeliveryPage from '@/pages/aliado/AliadoOkDeliveryPage';

const UserGuard = ({ children }) => {
    const { role, loading, mustChangePin } = useAuth();
    const location = useLocation();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
    if (role !== 'user') return <Navigate to="/login" replace state={{ from: location }} />;
    if (mustChangePin && location.pathname !== '/change-pin') return <Navigate to="/change-pin" replace />;
    return children;
};

// Guard for /panel — redirects unauthenticated users to /acceso
const PanelGuard = ({ children }) => {
    const { role, loading, mustChangePin } = useAuth();
    const location = useLocation();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
    if (role !== 'user') return <Navigate to="/acceso" replace state={{ from: location }} />;
    if (mustChangePin && location.pathname !== '/change-pin') return <Navigate to="/change-pin" replace />;
    return children;
};

const AdminGuard = ({ children }) => {
    const { role, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
    if (role !== 'superadmin') return <Navigate to="/superadmin" replace />;
    return children;
};

// Deep-link short redirects: /a/:id -> /ad/:id, /o/:id -> /orders/success?session_id=...
const ShortAdRedirect = () => {
    const { pathname } = useLocation();
    const id = pathname.split('/').pop();
    return <Navigate to={`/ad/${id}`} replace />;
};

function App() {
    return (
        <HelmetProvider>
        <div className="App">
            <I18nProvider>
                <ShareConfigProvider>
                    <AuthProvider>
                        <CartProvider>
                        <CategoriesProvider>
                        <BrowserRouter>
                            <LoginModalProvider>
                            <PostMessageBridge />
                            <Toaster position="top-center" richColors />
                            {/* Global floating widgets — always rendered for logged-in users */}
                            <OkBotChatWidget />
                            <OkChatWidget />
                            <Routes>
                            {/* Public */}
                            <Route path="/" element={<HomePage />} />
                            <Route path="/carrito" element={<CartPage />} />
                            <Route path="/widget/:id" element={<WidgetView />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/categories/:slug" element={<CategoryPage />} />
                            <Route path="/ad/:id" element={<AdDetailPage />} />
                            <Route path="/a/:id" element={<ShortAdRedirect />} />
                            <Route path="/profile/:nickname" element={<ProfilePage />} />
                            <Route path="/u/:nickname" element={<PublicUserPage />} />
                            <Route path="/store/:nickname" element={<StorefrontPage />} />
                            <Route path="/live/:id" element={<LivestreamPage />} />
                            <Route path="/orders/success" element={<OrderSuccessPage />} />

                            {/* Public services (Phase 16) */}
                            <Route path="/servicios" element={<ServicesHubPage />} />
                            <Route path="/servicios/prestador/:id" element={<ServiceProviderPage />} />

                            {/* Public health monitor (Phase 18) */}
                            <Route path="/status" element={<StatusPage />} />

                            {/* Public OkGo plans (Phase 20b) */}
                            <Route path="/planes" element={<PlansPage />} />
                            <Route path="/plans" element={<Navigate to="/planes" replace />} />

                            {/* Public landing: Super Poderes (Phase 20f) */}
                            <Route path="/superpoderes" element={<SuperpoderesPage />} />
                            <Route path="/online" element={<OnlinePage />} />

                            {/* OkGo Platform */}
                            <Route path="/okgo" element={<OkGoHomePage />} />
                            <Route path="/aliado" element={<OkNegocioRegister />} />
                            <Route path="/aliado/:slug" element={<OkNegocioProfile />} />

                            {/* OkNegocio dashboard */}
                            <Route path="/aliado/dashboard" element={<AliadoGuard><AliadoDashboardLayout /></AliadoGuard>}>
                                <Route index element={<AliadoDashboardHome />} />
                                <Route path="orders" element={<AliadoOrdersPage />} />
                                <Route path="products" element={<AliadoProductsPage />} />
                                <Route path="categories" element={<AliadoCategoriesPage />} />
                                <Route path="branches" element={<AliadoBranchesPage />} />
                                <Route path="sales" element={<AliadoSalesPage />} />
                                <Route path="squad" element={<AliadoSquadPage />} />
                                <Route path="cartas" element={<AliadoCartasPage />} />
                                <Route path="pasillos" element={<AliadoPasillosPage />} />
                                <Route path="settings" element={<AliadoSettingsPage />} />
                                <Route path="okdelivery" element={<AliadoOkDeliveryPage />} />
                            </Route>

                            {/* Auth users */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/acceso" element={<OkUserAcceso />} />
                            <Route path="/change-pin" element={<UserGuard><ChangePinPage /></UserGuard>} />

                            {/* User dashboard */}
                            <Route path="/dashboard" element={<UserGuard><DashboardLayout /></UserGuard>}>
                                <Route index element={<DashboardHome />} />
                                <Route path="ads" element={<MyAdsPage />} />
                                <Route path="ads/new" element={<NewAdPage />} />
                                <Route path="ads/:id/edit" element={<EditAdPage />} />
                                <Route path="favorites" element={<FavoritesPage />} />
                                <Route path="purchases" element={<PurchasesPage />} />
                                <Route path="sales" element={<SalesPage />} />
                                <Route path="ratings" element={<RatingsPage />} />
                                <Route path="benefits" element={<NextLevelHub />} />
                                <Route path="messages" element={<MessagesPage />} />
                                <Route path="moderation" element={<ModerationPage />} />
                                <Route path="profile" element={<ProfileEditPage />} />
                                <Route path="kyc" element={<KycPage />} />
                                <Route path="offers" element={<OffersPage />} />
                                <Route path="invoices" element={<InvoicesPage />} />
                                <Route path="pro" element={<Navigate to="/planes" replace />} />
                                <Route path="storefront" element={<DashboardStorefront />} />
                                <Route path="livestream" element={<DashboardLivestream />} />
                                <Route path="wallet" element={<DashboardWallet />} />
                                <Route path="cashback" element={<DashboardCashback />} />
                                <Route path="services" element={<DashboardServicesProvider />} />
                                <Route path="service-orders" element={<DashboardServiceOrders />} />
                                <Route path="envios" element={<DashboardEnvios />} />
                                <Route path="flash-sales" element={<DashboardFlashSales />} />
                                <Route path="services-provider" element={<DashboardMyServices />} />
                                <Route path="addresses" element={<DashboardAddresses />} />
                                <Route path="comisiones" element={<DashboardComisiones />} />
                                <Route path="impuestos" element={<DashboardImpuestos />} />
                                <Route path="okpro" element={<DashboardOkPro />} />
                                <Route path="seller-banners" element={<DashboardSellerBanners />} />
                                <Route path="lists" element={<ListsPage />} />
                            </Route>

                            {/* OkUser Panel — same sub-pages as /dashboard, no marketplace link, logout → /acceso */}
                            <Route path="/panel" element={<PanelGuard><PanelLayout /></PanelGuard>}>
                                <Route index element={<DashboardHome />} />
                                <Route path="ads" element={<MyAdsPage />} />
                                <Route path="ads/new" element={<NewAdPage />} />
                                <Route path="ads/:id/edit" element={<EditAdPage />} />
                                <Route path="favorites" element={<FavoritesPage />} />
                                <Route path="purchases" element={<PurchasesPage />} />
                                <Route path="sales" element={<SalesPage />} />
                                <Route path="ratings" element={<RatingsPage />} />
                                <Route path="benefits" element={<NextLevelHub />} />
                                <Route path="messages" element={<MessagesPage />} />
                                <Route path="moderation" element={<ModerationPage />} />
                                <Route path="profile" element={<ProfileEditPage />} />
                                <Route path="kyc" element={<KycPage />} />
                                <Route path="offers" element={<OffersPage />} />
                                <Route path="invoices" element={<InvoicesPage />} />
                                <Route path="storefront" element={<DashboardStorefront />} />
                                <Route path="livestream" element={<DashboardLivestream />} />
                                <Route path="wallet" element={<DashboardWallet />} />
                                <Route path="cashback" element={<DashboardCashback />} />
                                <Route path="service-orders" element={<DashboardServiceOrders />} />
                                <Route path="envios" element={<DashboardEnvios />} />
                                <Route path="flash-sales" element={<DashboardFlashSales />} />
                                <Route path="services-provider" element={<DashboardMyServices />} />
                                <Route path="addresses" element={<DashboardAddresses />} />
                                <Route path="comisiones" element={<DashboardComisiones />} />
                                <Route path="impuestos" element={<DashboardImpuestos />} />
                                <Route path="okpro" element={<DashboardOkPro />} />
                                <Route path="seller-banners" element={<DashboardSellerBanners />} />
                                <Route path="lists" element={<ListsPage />} />
                            </Route>

                            {/* Super admin */}
                            <Route path="/superadmin" element={<AdminLoginPage />} />
                            <Route element={<AdminGuard><SuperAdminLayout /></AdminGuard>}>
                                <Route path="/superadmin/dashboard" element={<AdminDashboard />} />
                                <Route path="/superadmin/users" element={<AdminUsers />} />
                                <Route path="/superadmin/ads" element={<AdminAds />} />
                                <Route path="/superadmin/categories" element={<AdminCategories />} />
                                <Route path="/superadmin/orders" element={<AdminOrders />} />
                                <Route path="/superadmin/banners" element={<AdminBanners />} />
                                <Route path="/superadmin/settings" element={<AdminSettings />} />
                                <Route path="/superadmin/oauth-sso" element={<AdminOAuthSSO />} />
                                <Route path="/superadmin/connect" element={<AdminConnect />} />
                                <Route path="/superadmin/api-connect" element={<AdminApiConnect />} />
                                <Route path="/superadmin/widgets" element={<AdminWidgets />} />
                                <Route path="/superadmin/kyc" element={<AdminKyc />} />
                                <Route path="/superadmin/reports" element={<AdminReports />} />
                                <Route path="/superadmin/flash-sales" element={<AdminFlashSales />} />
                                <Route path="/superadmin/factura" element={<AdminFactura />} />
                                <Route path="/superadmin/embed" element={<AdminEmbed />} />
                                <Route path="/superadmin/live" element={<AdminLive />} />
                                <Route path="/superadmin/storefronts" element={<AdminStorefront />} />
                                <Route path="/superadmin/wallet" element={<AdminWallet />} />
                                <Route path="/superadmin/cashback" element={<AdminCashback />} />
                                <Route path="/superadmin/services" element={<AdminServices />} />
                                <Route path="/superadmin/plans" element={<AdminPlans />} />
                                <Route path="/superadmin/envios" element={<AdminEnvios />} />
                                <Route path="/superadmin/comisiones" element={<AdminComisiones />} />
                                <Route path="/superadmin/impuestos" element={<AdminImpuestos />} />
                                <Route path="/superadmin/withdrawals" element={<AdminWithdrawals />} />
                                <Route path="/superadmin/okpro" element={<AdminOkPro />} />
                                <Route path="/superadmin/seller-banners" element={<AdminSellerBanners />} />
                                <Route path="/superadmin/ecartpay" element={<AdminEcartPay />} />
                                <Route path="/superadmin/okdelivery" element={<AdminOkDelivery />} />
                                <Route path="/superadmin/super-categorias" element={<AdminSuperCategorias />} />
                                <Route path="/superadmin/platform-apps" element={<AdminPlatformApps />} />
                                <Route path="/superadmin/okbot" element={<AdminOkBot />} />
                                <Route path="/superadmin/okchat" element={<AdminOkChat />} />
                                <Route path="/superadmin/oklovendo" element={<AdminOkLovendo />} />
                                <Route path="/superadmin/comisiones-okgo" element={<AdminComisionesOkGo />} />
                                <Route path="/superadmin/oknegocios" element={<AdminOkNegocios />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                            </LoginModalProvider>
                    </BrowserRouter>
                        </CategoriesProvider>
                        </CartProvider>
                </AuthProvider>
            </ShareConfigProvider>
            </I18nProvider>
        </div>
        </HelmetProvider>
    );
}

export default App;
